import React from 'react';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import { postNlEdit } from '../../../utilityFunctions/dashboardApi';

/**
 * Natural-language edit box. POST /{id}/edits {input_kind:"NL"} → { run_id }; the parent
 * mounts a RunProgressPanel for that run. 409 (stale base) → onStale() (parent reloads);
 * 422 / other errors are shown inline and the text is kept, so the old spec stays untouched.
 *
 * @param onRunStarted(runId, text)
 * @param onStale()
 */
export default function NlEditBox({ dashboardId, baseVersionId, jwt, disabled, onRunStarted, onStale }) {
  const { t } = useTranslation();
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const submit = async () => {
    const value = text.trim();
    if (!value || busy || disabled || !baseVersionId) return;
    setBusy(true);
    setError(null);
    const res = await postNlEdit(dashboardId, baseVersionId, value, jwt);
    setBusy(false);
    if (res.ok && res.data && res.data.run_id != null) {
      setText('');
      if (onRunStarted) onRunStarted(res.data.run_id, value);
      return;
    }
    if (res.status === 409) {
      if (onStale) onStale();
      return;
    }
    setError((res.data && (res.data.message || res.data.error)) || t('Dashboards.nlEdit.failed'));
  };

  return (
    <Box sx={{ mb: 2, minWidth: 0 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 0 }}>
        <TextField
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={t('Dashboards.nlEdit.placeholder')}
          size="small"
          multiline
          maxRows={4}
          fullWidth
          disabled={busy || disabled}
          sx={{ bgcolor: '#fff' }}
          inputProps={{ 'aria-label': t('Dashboards.nlEdit.placeholder') }}
        />
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || disabled || !text.trim()}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
          sx={{ whiteSpace: 'nowrap', textTransform: 'none', minHeight: 40 }}
        >
          {busy ? t('Dashboards.nlEdit.submitting') : t('Dashboards.nlEdit.submit')}
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{t('Dashboards.nlEdit.hint')}</Typography>
      {error && <Alert severity="error" sx={{ mt: 1, overflowWrap: 'anywhere' }} onClose={() => setError(null)}>{error}</Alert>}
    </Box>
  );
}
